// Draft model of the Cable sizing study form: what the user is typing, kept apart from the
// component so the payload rules sit next to the draft they read. Every value is held as the
// string the user typed; the three switches are booleans.

import { z } from "zod";

import {
  cableConstructionSchema,
  circuitSystemSchema,
  conductorArrangementSchema,
  conductorMaterialSchema,
  installationMethodSchema,
  insulationMaterialSchema,
  jurisdictionProfileSchema,
  protectiveConductorTypeSchema,
} from "../services/cableContract";

// Where a cable study's short-circuit current comes from (A28 (e)): the Ik'' of a stored
// EOS-04 fault run, or the figure the engineer typed. Exactly one of the two reaches the
// request, so the backend is never given both.
export const faultCurrentSourceSchema = z.enum(["TYPED", "LINKED_FAULT_RUN"]);

export type FaultCurrentSource = z.infer<typeof faultCurrentSourceSchema>;

export const FAULT_CURRENT_SOURCE_LABELS: Record<FaultCurrentSource, string> = {
  TYPED: "Typed fault current",
  LINKED_FAULT_RUN: "Linked fault run (EOS-04)",
};

export type CableSizingDraft = {
  studyCode: string;
  studyName: string;
  notes: string;
  /** The feeder picked from the revision's diagram, or "" (A28 (a)). */
  sldElementCode: string;
  /** Where the short-circuit current comes from (A28 (e)). */
  faultCurrentSource: FaultCurrentSource;
  /** The stored fault run the current is read from, or "" (A28 (e)). */
  faultRunId: string;
  designCurrentA: string;
  nominalVoltageV: string;
  routeLengthM: string;
  system: string;
  jurisdictionProfile: string;
  powerFactor: string;
  allowableVoltageDropPercent: string;
  faultCurrentKa: string;
  faultDurationS: string;
  harmonicNeutralFactor: string;
  conductorMaterial: string;
  insulationMaterial: string;
  construction: string;
  arrangement: string;
  numberOfLoadedConductors: string;
  parallelRuns: string;
  neutralRequired: boolean;
  reducedNeutralPermitted: boolean;
  protectiveConductorType: string;
  armoured: boolean;
  method: string;
  ambientTemperatureC: string;
  ambientDeratingFactor: string;
  groupingDeratingFactor: string;
  thermalInsulationFactor: string;
  depthDeratingFactor: string;
  soilThermalResistivityFactor: string;
  groupedCircuits: string;
  burialDepthM: string;
  soilThermalResistivityKMPerW: string;
  conductorSpacingMm: string;
  phaseSizesMm2: string;
  neutralSizesMm2: string;
  protectiveSizesMm2: string;
};

export function createInitialCableSizingDraft(): CableSizingDraft {
  return {
    studyCode: "",
    studyName: "",
    notes: "",
    sldElementCode: "",
    faultCurrentSource: "TYPED",
    faultRunId: "",
    designCurrentA: "",
    nominalVoltageV: "",
    routeLengthM: "",
    system: "",
    jurisdictionProfile: "IN",
    powerFactor: "",
    allowableVoltageDropPercent: "",
    faultCurrentKa: "",
    faultDurationS: "",
    harmonicNeutralFactor: "",
    conductorMaterial: "",
    insulationMaterial: "",
    construction: "",
    arrangement: "",
    numberOfLoadedConductors: "",
    parallelRuns: "",
    neutralRequired: true,
    reducedNeutralPermitted: false,
    protectiveConductorType: "",
    armoured: false,
    method: "",
    ambientTemperatureC: "",
    ambientDeratingFactor: "",
    groupingDeratingFactor: "",
    thermalInsulationFactor: "",
    depthDeratingFactor: "",
    soilThermalResistivityFactor: "",
    groupedCircuits: "",
    burialDepthM: "",
    soilThermalResistivityKMPerW: "",
    conductorSpacingMm: "",
    phaseSizesMm2: "",
    neutralSizesMm2: "",
    protectiveSizesMm2: "",
  };
}

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

// The shape a draft kept in the browser tab must still have to be restored: exactly the
// fields above, typed text as strings, the switches as booleans and the choices as values
// the contract knows or "". A draft saved by an older form fails here and is dropped
// instead of half-filling this one.
export const cableSizingDraftSchema = z
  .object({
    studyCode: z.string(),
    studyName: z.string(),
    notes: z.string(),
    // A draft kept before the field existed comes back as it was, with no feeder chosen.
    sldElementCode: z.string().default(""),
    // The same for the fault-run link: an older draft comes back with a typed current.
    faultCurrentSource: faultCurrentSourceSchema.default("TYPED"),
    faultRunId: z.string().default(""),
    designCurrentA: z.string(),
    nominalVoltageV: z.string(),
    routeLengthM: z.string(),
    system: chosenOrBlank(circuitSystemSchema),
    jurisdictionProfile: jurisdictionProfileSchema,
    powerFactor: z.string(),
    allowableVoltageDropPercent: z.string(),
    faultCurrentKa: z.string(),
    faultDurationS: z.string(),
    harmonicNeutralFactor: z.string(),
    conductorMaterial: chosenOrBlank(conductorMaterialSchema),
    insulationMaterial: chosenOrBlank(insulationMaterialSchema),
    construction: chosenOrBlank(cableConstructionSchema),
    arrangement: chosenOrBlank(conductorArrangementSchema),
    numberOfLoadedConductors: z.string(),
    parallelRuns: z.string(),
    neutralRequired: z.boolean(),
    reducedNeutralPermitted: z.boolean(),
    protectiveConductorType: chosenOrBlank(protectiveConductorTypeSchema),
    armoured: z.boolean(),
    method: chosenOrBlank(installationMethodSchema),
    ambientTemperatureC: z.string(),
    ambientDeratingFactor: z.string(),
    groupingDeratingFactor: z.string(),
    thermalInsulationFactor: z.string(),
    depthDeratingFactor: z.string(),
    soilThermalResistivityFactor: z.string(),
    groupedCircuits: z.string(),
    burialDepthM: z.string(),
    soilThermalResistivityKMPerW: z.string(),
    conductorSpacingMm: z.string(),
    phaseSizesMm2: z.string(),
    neutralSizesMm2: z.string(),
    protectiveSizesMm2: z.string(),
  })
  .strict();

/** Empty text means "not supplied": the backend default applies. */
function optional(value: string): string | undefined {
  return value.trim() === "" ? undefined : value.trim();
}

function optionalInt(value: string): number | undefined {
  const text = value.trim();
  return text === "" ? undefined : Number(text);
}

/** Drop undefined entries so "not supplied" fields are absent, not null-ish. */
function compact<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined),
  ) as T;
}

/** Comma-separated sizes -> trimmed exact-decimal strings; empty -> undefined. */
function sizeList(value: string): string[] | undefined {
  const items = value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");
  return items.length === 0 ? undefined : items;
}

/** The request body as the form sends it; validated afterwards with cableSizingRequestSchema. */
export function buildCableSizingPayload(draft: CableSizingDraft) {
  return compact({
    code: draft.studyCode,
    name: draft.studyName,
    jurisdiction_profile: draft.jurisdictionProfile,
    circuit: compact({
      design_current_a: draft.designCurrentA,
      nominal_voltage_v: draft.nominalVoltageV,
      route_length_m: draft.routeLengthM,
      system: draft.system,
      power_factor: optional(draft.powerFactor),
      allowable_voltage_drop_percent: optional(draft.allowableVoltageDropPercent),
      // One source only: a linked run sends no typed figure (A28 (e)).
      fault_current_ka:
        draft.faultCurrentSource === "LINKED_FAULT_RUN" ? undefined : optional(draft.faultCurrentKa),
      fault_duration_s: optional(draft.faultDurationS),
      harmonic_neutral_factor: optional(draft.harmonicNeutralFactor),
    }),
    cable: compact({
      conductor_material: draft.conductorMaterial,
      insulation_material: draft.insulationMaterial,
      construction: draft.construction,
      arrangement: draft.arrangement,
      number_of_loaded_conductors: Number(draft.numberOfLoadedConductors),
      parallel_runs: optionalInt(draft.parallelRuns),
      neutral_required: draft.neutralRequired,
      reduced_neutral_permitted: draft.reducedNeutralPermitted,
      protective_conductor_type: optional(draft.protectiveConductorType),
      armoured: draft.armoured,
    }),
    installation: compact({
      method: draft.method,
      ambient_temperature_c: draft.ambientTemperatureC,
      ambient_derating_factor: optional(draft.ambientDeratingFactor),
      grouping_derating_factor: optional(draft.groupingDeratingFactor),
      thermal_insulation_factor: optional(draft.thermalInsulationFactor),
      depth_derating_factor: optional(draft.depthDeratingFactor),
      soil_thermal_resistivity_factor: optional(draft.soilThermalResistivityFactor),
      grouped_circuits: optionalInt(draft.groupedCircuits),
      burial_depth_m: optional(draft.burialDepthM),
      soil_thermal_resistivity_k_m_per_w: optional(draft.soilThermalResistivityKMPerW),
      conductor_spacing_mm: optional(draft.conductorSpacingMm),
    }),
    size_schedule: compact({
      phase_sizes_mm2: sizeList(draft.phaseSizesMm2) ?? [],
      neutral_sizes_mm2: sizeList(draft.neutralSizesMm2),
      protective_sizes_mm2: sizeList(draft.protectiveSizesMm2),
    }),
    notes: optional(draft.notes),
    sld_element_code: optional(draft.sldElementCode),
    fault_run_id:
      draft.faultCurrentSource === "LINKED_FAULT_RUN" ? optional(draft.faultRunId) : undefined,
  });
}
